import React from "react";

export interface PinKeypadProps {
  /** Current value (digits only when pinOnly) */
  value: string;
  /** Called when value changes (e.g. user taps digit or delete) */
  onChange: (value: string) => void;
  /** Max length (default 8 for PIN) */
  maxLength?: number;
  /** PIN-only mode: hide decimal button and only allow digits */
  pinOnly?: boolean;
  /** Optional class for the container */
  className?: string;
}

const ROWS: (string | "backspace" | "decimal")[][] = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["decimal", "0", "backspace"],
];

export const PinKeypad: React.FC<PinKeypadProps> = ({
  value,
  onChange,
  maxLength = 8,
  pinOnly = true,
  className = "",
}) => {
  const handleKey = (key: string | "backspace" | "decimal") => {
    if (key === "backspace") {
      onChange(value.slice(0, -1));
      return;
    }
    if (key === "decimal") {
      if (!pinOnly) onChange(value + ".");
      return;
    }
    if (value.length < maxLength) {
      onChange(value + key);
    }
  };

  const gap = "gap-2";
  const size = "w-[60px] h-[60px] min-w-[60px] min-h-[60px]"; // fixed square 60×60
  const btnClass =
    `flex items-center justify-center ${size} rounded-xl bg-white border-2 border-[#885ABB] text-[#402B69] font-bold text-base hover:bg-purple-50 active:scale-[0.98] transition-all`;
  const deleteBtnClass =
    `flex items-center justify-center ${size} rounded-xl bg-red-50 border-2 border-red-200 hover:bg-red-100 hover:border-red-300 active:scale-[0.98] transition-all`;
  const placeholderClass = size;

  return (
    <div className={`grid grid-cols-3 ${gap} w-fit mx-auto ${className}`}>
      {ROWS.map((row, rowIdx) => (
        <React.Fragment key={rowIdx}>
          {row.map((cell, colIdx) => {
            if (cell === "backspace") {
              return (
                <button
                  key="backspace"
                  type="button"
                  onClick={() => handleKey("backspace")}
                  className={deleteBtnClass}
                  aria-label="Delete"
                >
                  <span className="text-red-500 font-bold text-lg">✕</span>
                </button>
              );
            }
            if (cell === "decimal") {
              if (pinOnly) {
                return <div key="decimal-placeholder" className={placeholderClass} />;
              }
              return (
                <button
                  key="decimal"
                  type="button"
                  onClick={() => handleKey("decimal")}
                  className={btnClass}
                >
                  .
                </button>
              );
            }
            return (
              <button
                key={cell}
                type="button"
                onClick={() => handleKey(cell)}
                className={btnClass}
              >
                {cell}
              </button>
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

export default PinKeypad;
